// app/src/lib/demo.js — demo-mode obfuscation for public walkthroughs.
//
// When on, this makes real restaurant data safe to show to a room:
//   • dollar amounts are divided by 1.9 (scale) so the dashboard reads like a
//     smaller restaurant. Ratios/percentages are unchanged (both sides scale).
//   • IDENTIFYING NAMES are swapped for fictional ones (obscure) — the business
//     name, bank/card merchants, COGS vendors, and card nicknames/masks. This is
//     the part that scaling alone does NOT do: ÷1.9 hides the dollars, not who
//     you bank with or buy from, and that identity is the sensitive bit in a
//     public meetup. Generic strings (ingredient names, channel names like
//     DoorDash, category labels) are left alone — they aren't identifying and
//     they help the demo read as real.
//
// The swap is DETERMINISTIC: a given real name always maps to the same fake, so
// the same merchant reads identically across Today, Cashflow, Reports and
// Spending. Screenshots taken with demo mode on are safe to share.
//
// SUBSCRIPTION PRICING IS NOT SCALED — the Plans/Subscribe screen uses its own
// local money formatter that bypasses this module.

const KEY = 'kanjo_demo_mode_v1';
const FACTOR = 1.9;
let cached = null;

function read() {
  try { return typeof localStorage !== 'undefined' && localStorage.getItem(KEY) === '1'; } catch { return false; }
}

export function isOn() {
  if (cached === null) cached = read();
  return cached;
}

export function setOn(on) {
  cached = !!on;
  try { localStorage.setItem(KEY, on ? '1' : ''); } catch { /* ignore */ }
}

/** Scale operational cents in demo mode; passthrough when off (or when cents falsy). */
export function scale(cents) {
  if (!isOn()) return cents || 0;
  return (cents || 0) / FACTOR;
}

// ── Name obfuscation ────────────────────────────────────────────────────────
// Fictional names. Deliberately generic and clearly not-real (per the
// anonymize-placeholders rule): no real vendor, bank, or business names.
//
// Suppliers are built COMBINATORIALLY from two pools (place × trade), giving a
// few hundred distinct names from ~20 words. That keeps the mapping virtually
// collision-free across a restaurant's dozen-odd vendors — a demo where two
// different vendors read as the same name looks wrong — while staying compact.
const SUP_PLACE = [
  'Harbor', 'Cornerstone', 'Riverside', 'Blue Line', 'Anchor', 'Summit',
  'Greenfield', 'Portside', 'Maple & Oak', 'Ironwood', 'Twin Pines', 'Delta',
  'Cedar Creek', 'Northgate', 'Silver Fork', 'Baytown', 'Willow Lane', 'Granite',
];
const SUP_TRADE = [
  'Produce Co', 'Foods', 'Provisions', 'Supply', 'Distributing', 'Wholesale',
  'Market', 'Trading Co', 'Purveyors', 'Depot', 'Grocers', 'Farms',
];   // 18 × 12 = 216 combinations
const ACCOUNTS = ['Business Checking', 'Operating Card', 'Rewards Card', 'Reserve Savings'];
const BUSINESS = 'Copper Kettle Kitchen';   // the single demo persona

// FNV-1a — stable across reloads, so the same real name always maps to the same
// fake everywhere it appears.
function hash(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}

/**
 * Replace an identifying name with a fictional one when demo mode is on.
 * Passthrough when off, or when there's nothing to hide.
 *
 * @param {string} name  the real value
 * @param {'merchant'|'vendor'|'business'|'account'|'mask'} [kind]
 */
export function obscure(name, kind = 'merchant') {
  if (!isOn()) return name;
  const s = String(name == null ? '' : name).trim();
  if (!s) return name;                      // nothing to hide → don't invent one
  if (kind === 'business') return BUSINESS; // one restaurant, one name
  const h = hash(s.toLowerCase());
  if (kind === 'account') return ACCOUNTS[h % ACCOUNTS.length];
  if (kind === 'mask') return String(h % 9000 + 1000); // stable fake last-4
  // merchant / vendor: place × trade
  return `${SUP_PLACE[h % SUP_PLACE.length]} ${SUP_TRADE[Math.floor(h / SUP_PLACE.length) % SUP_TRADE.length]}`;
}

/**
 * The COGS-supplier counterpart to cashflow/util.js's cleanTxnName: ONE place
 * where a vendor record's name becomes a name to put on screen.
 *
 * Bank and card merchants have run through cleanTxnName since demo mode
 * shipped, but supplier names arrive by a different road — scanned invoices
 * into db.vendors — and had no equivalent, so Price changes, Receipts, the
 * order guide and the rest printed real suppliers with demo mode on.
 *
 * Call this wherever a vendor NAME is being prepared for display. Do NOT call
 * it where the name is a key: matching, dedup, price keys (name × vendor ×
 * unit), invoice reassignment, exports and backups all need the real value, and
 * an export of fictional names would be worse than the leak.
 *
 * @param {string} name the vendor's stored name
 * @param {string} [fallback] shown when the vendor is unknown; never obscured,
 *   since it identifies nobody
 */
export function vendorLabel(name, fallback = 'Unknown vendor') {
  const s = String(name == null ? '' : name).trim();
  if (!s) return fallback;
  return obscure(s, 'vendor');
}
